import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux';
import styled from 'styled-components'
import { Icon } from '@iconify/react';
import timesSolid from '@iconify/icons-la/times-solid';
import moment from 'moment-timezone'
import { colour_lookup, connectivity_colour_lookup } from '../../common/Colours'
import { getHost } from '../../../api/host'
import { cloneDeep } from 'lodash';
import { isUndefined, notUndefined, isUndefinedOrNull } from '../../../lib/typeCheck';
import { fetchPositionTimePeriods, loadPositions, fetchPositionSamples } from '../../../store/positions/positionsSlice';


export const StatusContainer = styled.div`
  max-height: 100%;
  width: 380px;
  background: transparent;
  position: absolute;
  top: 0;
  right: 0;
  overflow-y: scroll;
  display: block;
`;

const StyledStatusBox = styled.div`
  display: block;
  width: 350px;
  background: #FFF;
  margin: 10px auto;
  position: relative;
  overflow: auto;
  padding: 10px 20px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
`;

const Close = styled.div`
  position: absolute;
  top: 12px;
  right: 10px;
  padding: 5px;
  cursor: pointer;
  color: #555;
  
  &:hover {
    color: #333;
  }
`;

const StatusTitle = styled.div`
  display: block;
  position: relative;
  margin: 10px 30px 15px 10px;
  color: #333;
  font-size: 20px;
`;

const StatusLabel = styled.div`
  margin: 10px;
  color: #444;
  font-size: 16px;
`;

const StatusExceedanceBlock = styled.div`
  margin: 10px;
  color: #444;
  font-size: 14px;
`;

const StatusExceedance = styled.div`
  margin: 8px;
  display: flex;
`;

const StatusLimit = styled.div`
  margin: 12px 8px;
`;

const LimitTitle = styled.div`
  display: flex;
`;

const ExceedanceColour = styled.div<{ color: string }>`
  margin-left: 10px;
  margin-top: 2px;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background: ${props => props.color};
`;

const ExceedanceText = styled.div<{ color: string }>`
  margin-left: 10px;
  margin-top: 0px;
  color: ${props => props.color};
`;

const MoreDetails = styled.a`
  display: block;
  margin 10px 0 10px 0;
  padding: 8px 10px 8px 10px;
  background: #233567;
  font-size: 16px;
  text-align: center;
  border-radius: 10px;
  cursor: pointer;
  color: #FFF;
  text-decoration: none;
  text-align: center;
  
  a {
    cursor: pointer;
    color: #FFF;
    text-decoration: none;
    text-align: center;
    
    &:hover {
      color: #DDD;
    }
  }
`;

const StyledExceedanceBar = styled.div`

`;

const Small = styled.span`
  font-size: 14px;
`;


const ExceedanceBar = ({ summary }: {summary: any}) => {
  
    return (
      <StyledExceedanceBar>
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 20'>
          {summary.map((state, index) => (
            <rect x={10 * index} y='0' height='20' width='5' fill={colour_lookup(state)} />
          ))}
        </svg>
      </StyledExceedanceBar>
    )
}

const makeGraphUrl = (position) => {
  if (position.type_id === 2 && position.subtype_id === 0) return `https://${getHost()}/ppv/overview/${position.station_id}`;
  return `https://${getHost()}/positions/graph/${position.station_id}`;
}

const capitalise = (s) => {
  if (typeof s !== 'string') return ''
  return s.replace(/_/g, ' ').split(' ').map((c, key) => (c.charAt(0).toUpperCase() + c.slice(1))).join(' ');
}

const currentLimitStatus = (limit) => {
  if (limit.exceedances.length === 0) return 'green';
  let exceedance_unix = moment(limit.exceedances[0].timestamp).unix();
  let threshold_unix = moment().unix() - limit.timebase;
  if (exceedance_unix > threshold_unix) return limit.exceedances[0].state;
  return 'green';
}

const exceedanceTextLookup = {
  green: 'OK',
  amber: 'Amber',
  red: 'Exceedance'
}

const makeLimitName = (limits, current_limit) => {
  
  if (isUndefinedOrNull(current_limit.channel)) {
    return `${current_limit.id == 0? 'Primary Limit': `Limit ${parseInt(current_limit.id) + 1}`} - ${capitalise(current_limit.type)}`;
  }
  
  if (current_limit.channel === 0 || current_limit.channel === 1) {
    
    let filtered_limits = limits.filter((limit, key) => limit.channel === current_limit.channel)
    let primary = current_limit.id === filtered_limits[0].id? true : false;
    let limit_index = 0;
    filtered_limits.forEach((filtered_limit, index) => {
      if (filtered_limit.id !== current_limit.id) {
        limit_index = index;
      }
    });
    
    return `${primary? `Primary Limit ${current_limit.channel === 0? '(M) ':'(B) '}`: `Limit ${limit_index + 1} ${current_limit.channel === 0? '(M) ':'(B) '}`} - ${capitalise(current_limit.type)}`;
  }
}

export const PositionStatusBox = ({ position, onClose }: { position: any, onClose: any }) => {

  const dispatch = useDispatch()    
  useEffect(() => {
    // Trigger time period retrieval on first load
    console.log("Initial fetching time periods");
    dispatch(fetchPositionTimePeriods(position));

    // Re-trigger time period retrieval every minute thereafter
    const interval = setInterval(() => {
      console.log("Fetching time periods");
      dispatch(fetchPositionTimePeriods(position));
    }, 60000);
    return () => clearInterval(interval);
  }, [])
  // dispatch(fetchPositionSamples(position));
  
  if (isUndefined(position)) {
    return null;
  }
  
  let current_timebase_exceedances = [];
  let previous_timebase_exceedances = [];
  
  if (notUndefined(position.current_timebase) && position.current_timebase) {
    current_timebase_exceedances = cloneDeep(position.current_timebase.exceedances);
  }
  
  if (notUndefined(position.previous_timebases) && position.previous_timebases !== null) {
    previous_timebase_exceedances = cloneDeep(position.previous_timebases.exceedances);
  }
  
  let exceedances = [].concat.apply([], [current_timebase_exceedances, previous_timebase_exceedances]).slice(0,5);
  let limits = position.limit_exceedance_summary || [];
  
  return (
    <StyledStatusBox>
      <Close onClick={() => { onClose(position) }}><Icon width='25px' height='25px' icon={timesSolid} /></Close>
      <StatusTitle>{position.title}</StatusTitle>
      <StatusLabel>Last Data</StatusLabel>
      <StatusExceedanceBlock>
        {/* <StatusExceedance><div>{moment(position.last_sample).format('YYYY/MM/DD HH:mm')}</div><ExceedanceColour color={connectivity_colour_lookup(position.online_status)}/></StatusExceedance> */}
        <StatusExceedance><div>{moment(position.status.last_sample).format('YYYY/MM/DD HH:mm')}</div><ExceedanceColour color={connectivity_colour_lookup(position.status.online_status)}/></StatusExceedance>
      </StatusExceedanceBlock>
      <StatusLabel>Exceedance Status <Small>(Up to 2 shown)</Small></StatusLabel>
      <StatusExceedanceBlock>
        {limits.length === 0? <StatusExceedance>None</StatusExceedance> : null}
        {limits.map((limit, index) => (
          limit.exceedances.length > 0?
            <StatusLimit key={index}>
              <LimitTitle>
                <div>{makeLimitName(limits, limit)}</div>
                <ExceedanceText color={colour_lookup(currentLimitStatus(limit))}>{exceedanceTextLookup[currentLimitStatus(limit)]}</ExceedanceText>
              </LimitTitle>
              {limit.exceedances.slice(0,2).map((exceedance, key) => (
                <StatusExceedance>
                  <div>{moment(exceedance.timestamp).format('YYYY/MM/DD HH:mm')}</div>
                  <ExceedanceColour color={colour_lookup(exceedance.state)}/>
                </StatusExceedance>
              ))}
            </StatusLimit>
          :
          <StatusLimit key={index}>
            <LimitTitle>
              <div>{makeLimitName(limits, limit)}</div>
              <ExceedanceText color={colour_lookup(currentLimitStatus(limit))}>{exceedanceTextLookup[currentLimitStatus(limit)]}</ExceedanceText>
            </LimitTitle>
          </StatusLimit>
        ))}
      </StatusExceedanceBlock>
      {false && <ExceedanceBar summary={position.timebases[2]} />}
      <MoreDetails onClick={(e) => { e.preventDefault(); window.open(makeGraphUrl(position), "_blank"); }}>
        Detailed View
      </MoreDetails>
    </StyledStatusBox>
  )
}